import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useForm } from "@refinedev/react-hook-form";
import { FieldValues } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "../../../../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../../../../store/useMarketplaceStore";

type ExceptionAddDialogProps = {
  open: boolean;
  onClose: () => void;
};

export type ExceptionFormValues = {
  competitorSellerId: string;
};

export const ExceptionAddDialog = (props: ExceptionAddDialogProps) => {
  const { open, onClose } = props;

  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.stores.competitionSettings.competitionExceptionList.addDialog",
  );

  const {
    reset,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ExceptionFormValues>({
    warnWhenUnsavedChanges: false,
  });

  const { mutate, isLoading } = useCustomMutation();
  const invalidate = useInvalidate();

  const onSubmit = (data: FieldValues) => {
    mutate(
      {
        url: `store/${selectedCountry?.id}/competitors`,
        method: "post",
        values: data,
        successNotification: () => {
          return {
            message: translate("saveMessage"),
            type: "success",
          };
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "all",
            invalidates: ["all"],
          });

          handleClose();
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>{translate("title")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          placeholder={translate("sellerIdHint")}
          fullWidth
          multiline
          variant="outlined"
          error={!!errors.competitorSellerId}
          helperText={
            errors.competitorSellerId ? translate("sellerIdError") : ""
          }
          maxRows={8}
          {...register("competitorSellerId", {
            required: true,
          })}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={handleClose}>
          {translateWithoutPrefix("buttons.cancel")}
        </Button>
        <LoadingButton type="submit" loading={isLoading}>
          {translateWithoutPrefix("buttons.save")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
